import React from 'react';
import Field, { isLinkField } from '../fields/Field';
import VideosSection from '../fields/VideosSection';
import { splitVideoFields } from '../fields/videoLayout';
import MusicSection from '../fields/MusicSection';
import { splitMusicFields } from '../fields/musicLayout';
import { groupOf } from '../state/emptyDoc';
import { usePreferences } from '../state/usePreferences';

/**
 * Renders whatever the server describes: the title, the question, then the
 * fields in the order they arrived. No section knows what it is.
 */
// Groups fields into cards: each `display` field opens one and the fields
// after it belong to it. Fields before the first display stay ungrouped.
const groupFields = fields => {
  const groups = [];
  fields.forEach(field => {
    if (field.kind === 'display' || !groups.length) {
      groups.push({
        lead: field.kind === 'display' ? field : null,
        items: []
      });
      if (field.kind === 'display') return;
    }
    groups[groups.length - 1].items.push(field);
  });
  return groups;
};
const Section = ({
  section,
  isPhone,
  isLast,
  footer
}) => {
  const {
    doc,
    locked,
    egComment,
    togglePick,
    setValue,
    setText
  } = usePreferences();
  if (!section) return null;
  const group = groupOf(doc, section.key);
  // A section that both picks playlists and reads videos out of them is the
  // Videos section: two tabs, not a flat list of fields.
  const {
    isTabbed,
    rest
  } = splitVideoFields(section);
  // Cards that group language variants: the Music section.
  const music = splitMusicFields(section);
  const groups = groupFields(isTabbed ? rest : music.isMusic ? music.rest : section.fields || []);
  const firstLed = groups.findIndex(g => g.lead);
  // A card carries the question only when it opens the section; otherwise the
  // question stays under the heading.
  const questionTitles = firstLed === 0;
  // A choice field prints the question itself, above its pills.
  const hasChoice = (section.fields || []).some(f => f.kind === 'choice');
  // The last section closes on a display field with nothing to answer: that
  // card is the one the submit button belongs in.
  const closing = groups.length - 1;
  const mergeSubmit = !!footer && closing >= 0 && !!groups[closing].lead && !groups[closing].items.length;
  return React.createElement("div", {
    className: "cp-col cp-col--narrow"
  }, React.createElement("h1", {
    className: "cp-h1"
  }, section.title), section.question && !hasChoice && !questionTitles && React.createElement("p", {
    className: "cp-subline"
  }, section.question), isTabbed && React.createElement(VideosSection, {
    section: section,
    group: group,
    locked: locked,
    isPhone: isPhone,
    onToggle: (fieldKey, id, cap) => togglePick(section.key, fieldKey, id, cap)
  }), music.isMusic && React.createElement(MusicSection, {
    section: section,
    group: group,
    locked: locked,
    onToggle: (fieldKey, id, cap) => togglePick(section.key, fieldKey, id, cap),
    onText: (fieldKey, value) => setText(section.key, fieldKey, value)
  }), groups.map((grp, gi) => {
    const fields = grp.items.map(field => React.createElement(Field, {
      key: field.key,
      section: section,
      field: field,
      value: group[field.key],
      group: group,
      locked: locked,
      isPhone: isPhone,
      paired: grp.items.length > 1 && grp.items.every(f => f.kind === 'text'),
      hideLabel: grp.items.length === 1 && isLinkField(field),
      onToggle: togglePick,
      onText: setText,
      onValue: setValue
    }));
    if (!grp.lead) return React.createElement(React.Fragment, {
      key: "lead-none"
    }, fields);

    // The lead's reveal rule governs the card: an unrevealed one must
    // not leave an empty titled shell behind.
    const reveal = grp.lead.reveal_when;
    if (reveal && String(group[reveal.field] ?? '') !== String(reveal.value)) {
      return null;
    }

    // The first card carries the section's question and the EG note.
    const first = gi === firstLed;
    // The closing card holds the submit button, so it is titled for it
    // and carries no Optional note.
    const isSubmitCard = mergeSubmit && gi === closing;
    // The first card is titled by the section's question; the rest by
    // the display field that opens them.
    const title = isSubmitCard ? grp.lead.label || 'Submit Creative Preferences' : first && questionTitles ? section.question : grp.lead.label;
    // Two or more inputs in one card sit side by side, as Bride/Groom do.
    const paired = grp.items.length > 1 && grp.items.every(f => f.kind === 'text');
    // A lone link under a picker reads as a hint plus a field, not a card.
    const isLink = grp.items.length === 1 && isLinkField(grp.items[0]);
    return React.createElement("div", {
      className: isLink ? 'cp-linkcard' : 'cp-notecard',
      key: grp.lead.key
    }, !isLink && title && React.createElement("p", {
      className: "cp-notehead"
    }, title), !isLink && section.optional && !isSubmitCard && React.createElement("p", {
      className: "cp-optional"
    }, "Optional"), React.createElement(Field, {
      section: section,
      field: grp.lead,
      group: group,
      locked: locked,
      isPhone: isPhone,
      bare: true
    }), paired ? React.createElement("div", {
      className: `cp-iggrid${isPhone ? ' is-phone' : ''}`
    }, fields) : fields, first && isLast && egComment && React.createElement("div", {
      className: "cp-egcomment"
    }, React.createElement("span", {
      className: "cp-egmark"
    }, "EG"), React.createElement("div", null, React.createElement("p", {
      className: "cp-eyebrow"
    }, "Comment by EG"), React.createElement("p", {
      className: "cp-egbody"
    }, egComment))), isSubmitCard && footer);
  }), !mergeSubmit && footer);
};
export default Section;
